import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Progress.css';
import logo from "../../assets/fishtagged-logo.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleRight, faAngleLeft, faCheck, faLock, faTrash, faTimes,
  faExclamationTriangle, faPlus, faRotateRight, faTriangleExclamation
} from "@fortawesome/free-solid-svg-icons";
import Hamburger from '../components/Hamburger';
import { getReports, deleteReport, loadReportIntoSession, getReportLabel } from '../../reportStorage';

const STEPS = [
  { id: 1, label: "Anonymity",             route: "/anonymity"   },
  { id: 2, label: "Upload Images",          route: "/uploadFiles" },
  { id: 3, label: "Report Image",           route: "/reportImage" },
  { id: 4, label: "Fish Information",       route: "/fishInfo"    },
  { id: 5, label: "Final Review & Submit",  route: "/finalreview" },
];

function isStepComplete(stepId, report) {
  switch (stepId) {
    case 1: return !!(report.anonymityData?.observationType);
    case 2: return Array.isArray(report.imagesMeta) && report.imagesMeta.length > 0;
    case 3: {
      const d = report.reportImageData;
      if (!d) return false;
      const loc = d.noLocation ? !!d.landmark?.trim() : !!(d.latitude && d.longitude);
      return !!(d.dateTaken && d.timeTaken && loc);
    }
    case 4: {
      const d = report.fishInfoData;
      return !!(d?.species && d?.lenWhole && d?.lenUnit && d?.tagColor && d?.tagPrefix && d?.tagNumber);
    }
    case 5: return !!report.submitted;
    default: return false;
  }
}

function getResumeRoute(report) {
  for (let i = 0; i < STEPS.length; i++) {
    if (!isStepComplete(STEPS[i].id, report)) return STEPS[i].route;
  }
  return "/finalreview";
}

function UploadStatusBadge({ status }) {
  if (!status) return null;
  const isRelogin = status === 'reloginRequired';
  return (
    <div className={`p-upload-status-badge p-upload-status-badge--${isRelogin ? 'relogin' : 'failed'}`}>
      <FontAwesomeIcon icon={faTriangleExclamation} />
      <span>{isRelogin ? 'Re-login required' : 'Upload failed'}</span>
    </div>
  );
}

export default function Progress({ user }) {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const refreshReports = () => setReports(getReports().filter((r) => !r.submitted));

  useEffect(() => { refreshReports(); }, []);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prev = {
      htmlOverflow: html.style.overflow, htmlHeight: html.style.height,
      bodyOverflow: body.style.overflow, bodyHeight: body.style.height,
    };
    html.style.overflow = 'auto'; html.style.height = 'auto';
    body.style.overflow = 'auto'; body.style.height = 'auto';
    return () => {
      html.style.overflow = prev.htmlOverflow; html.style.height = prev.htmlHeight;
      body.style.overflow = prev.bodyOverflow; body.style.height = prev.bodyHeight;
    };
  }, []);

  const handleResume = (report) => {
    loadReportIntoSession(report);
    navigate(getResumeRoute(report));
  };

  const handleRetry = (report) => {
    const { markReportUploadStatus } = require('../../reportStorage');
    markReportUploadStatus(report.id, null);
    loadReportIntoSession(report);
    navigate('/finalreview');
  };

  const handleDeleteConfirm = () => {
    deleteReport(confirmDeleteId);
    refreshReports();
    setConfirmDeleteId(null);
  };

  const handleNewReport = () => {
    sessionStorage.removeItem("anonymityData");
    sessionStorage.removeItem("reportImageData");
    sessionStorage.removeItem("fishInfoData");
    sessionStorage.removeItem("activeReportId");
    sessionStorage.removeItem("optOutRewards");
    sessionStorage.removeItem("cameraLocation");
    sessionStorage.removeItem("cameraDatetime");
    navigate("/anonymity");
  };

  return (
    <div className="p-page">
      <div className="bg-image" />
      <div className="p-top-row">
        <div className="logo"><img src={logo} alt="FishTagged Logo" /></div>
        <div className="top-hamburger"><Hamburger user={user} /></div>
      </div>
      <div className="p-scroll-area">
        <div className="card-wrapper">
          <main className="p-card">
            <h1 className="p-title">Reports in Progress</h1>
            <p className="p-subtitle">
              {reports.length > 0 ? "Continue or manage your entries." : "No current reports in progress."}
            </p>
            {reports.length > 0 && (
              <div className="p-reports-list">
                {reports.map((report) => {
                  const hasUploadIssue = !!report.uploadStatus;
                  const isRelogin = report.uploadStatus === 'reloginRequired';

                  const stepStatuses = [];
                  let chainBroken = false;
                  for (const s of STEPS) {
                    const complete = !chainBroken && isStepComplete(s.id, report);
                    if (!complete) chainBroken = true;
                    stepStatuses.push({ ...s, complete });
                  }
                  const completedCount = stepStatuses.filter((s) => s.complete).length;
                  const label = getReportLabel(report);

                  return (
                    <div
                      key={report.id}
                      className={`fr-form-box p-report-box${hasUploadIssue ? ` p-report-box--${isRelogin ? 'relogin' : 'failed'}` : ''}`}
                    >
                      <div className="p-report-header">
                        <div className="fr-field-center p-report-title-row">
                          <span className="fr-field-label">{label}</span>
                        </div>
                        <button className="p-delete-btn" onClick={() => setConfirmDeleteId(report.id)} aria-label="Delete report">
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>

                      <UploadStatusBadge status={report.uploadStatus} />

                      <div className="fr-horizontal-line" />

                      {stepStatuses.map((step, idx) => {
                        const prevComplete = idx === 0 || stepStatuses[idx - 1].complete;
                        const isAccessible = prevComplete || step.complete;
                        return (
                          <div key={step.id} className={`p-step-row${step.complete ? " p-step-row--done" : ""}${!isAccessible ? " p-step-row--locked" : ""}`}>
                            <div className="p-step-badge">
                              {step.complete
                                ? <FontAwesomeIcon icon={faCheck} />
                                : !isAccessible ? <FontAwesomeIcon icon={faLock} />
                                : <span>{step.id}</span>}
                            </div>
                            <div className="p-step-text">
                              <span className="p-step-name">{step.label}</span>
                            </div>
                            <span className={`p-badge${step.complete ? " p-badge--done" : isAccessible ? " p-badge--pending" : " p-badge--locked"}`}>
                              {step.complete ? "Done" : isAccessible ? "Pending" : "Locked"}
                            </span>
                          </div>
                        );
                      })}

                      <div className="p-report-footer">
                        {hasUploadIssue ? (
                          <div className="p-footer-actions">
                            {isRelogin && (
                              <button className="fi-submit-pill p-resume-pill p-relogin-pill" onClick={() => navigate('/login')}>
                                <span className="fi-submit-text">Log In</span>
                                <FontAwesomeIcon icon={faAngleRight} className="submit-arrow" />
                              </button>
                            )}
                            <button className="fi-submit-pill p-resume-pill p-retry-pill" onClick={() => handleRetry(report)}>
                              <FontAwesomeIcon icon={faRotateRight} />
                              <span className="fi-submit-text">Retry Upload</span>
                              <FontAwesomeIcon icon={faAngleRight} className="submit-arrow" />
                            </button>
                          </div>
                        ) : (
                          <button className="fi-submit-pill p-resume-pill" onClick={() => handleResume(report)}>
                            <span className="fi-submit-text">
                              {completedCount === STEPS.length ? "Go to Final Review" : "Resume"}
                            </span>
                            <FontAwesomeIcon icon={faAngleRight} className="submit-arrow" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </main>
        </div>
      </div>

      {confirmDeleteId && (
        <div className="ri-modal-overlay">
          <div className="ri-modal">
            <FontAwesomeIcon icon={faTimes} className="ri-modal-close" onClick={() => setConfirmDeleteId(null)} />
            <FontAwesomeIcon icon={faExclamationTriangle} className="ri-warning-icon" />
            <h2 className="ri-modal-title">Delete Report?</h2>
            <p className="ri-modal-body">This cannot be undone. All progress for this entry will be lost.</p>
            <div className="ri-modal-actions">
              <button className="ri-modal-cancel" onClick={() => setConfirmDeleteId(null)}>Cancel</button>
              <button className="ri-modal-choose" onClick={handleDeleteConfirm}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
